import { useState } from 'react'
import PropTypes from 'prop-types'
import { useForm } from 'react-hook-form'
import { connect } from 'react-redux'
import { Tabs, Tab } from 'react-bootstrap'
import { Form, Input } from 'components/forms'
import { StyledSubHeading } from 'components/headings'
import { parseErrors } from 'lib/api-utils'
import { numberToCurrency } from 'lib/money'
import { SET_USER_CREDIT } from 'registry/actions'
import { applyUserCredit } from 'registry/api'
import { LinkButton } from 'components/buttons'
import { errorMessage, successMessage } from 'lib/flash-message'
import CreditHistoryTabContent from './components/credit-history-tab-content'
import GroupGiftFundsTabContent from './components/group-gift-funds-tab-content'
import Spinner from 'shared/spinner'

export const GiftCardsAndPromotions = ({
  activeCredits,
  balance,
  groupGiftTotalFundedAmount,
  groupGifts,
  isOwner,
  lineItems,
  setUserCredit,
}) => {
  const [submitting, setSubmitting] = useState(false)
  const [showApplyCreditForm, setShowApplyCreditForm] = useState(false)
  const { register, handleSubmit, reset } = useForm()

  const onSubmit = (data) => {
    let { code } = data
    setSubmitting(true)
    // Remove non-alphanumeric
    code = code.replace(/\W/g, '')
    // Format with hyphen separating every 4 characters
    code = (code.match(/.{1,4}/g) || []).join('-')

    applyUserCredit({ userCredit: { code } })
      .then((resp) => {
        if (resp.userCredit.isOwner) {
          successMessage(
            "We've applied that gift card/promo code to your account!"
          )
        } else {
          successMessage(
            "We've applied that gift card/promo code to the registry owner's account!"
          )
        }

        setUserCredit(resp)
        reset()
      })
      .catch((resp) => {
        // Sometimes this message includes html
        errorMessage(
          <span
            dangerouslySetInnerHTML={{ __html: parseErrors(resp)._error }}
          />
        )
      })
      .then(() => {
        setTimeout(() => {
          setSubmitting(false)
        }, 500)
      })
  }

  return (
    <div>
      <StyledSubHeading>
        <div className="flex align-end">
          <span className="flex grow">
            Babylist gift cards & group gift funds:
          </span>
          <span className="text-credit text-bold">
            {numberToCurrency(balance)}
          </span>
        </div>
      </StyledSubHeading>

      <p className="h6">
        <em>
          {isOwner ? (
            <>
              Your balance will be automatically applied during checkout from
              the Babylist shop.
            </>
          ) : (
            <>
              Please note, only the registry owner may spend the gift card
              balance.
            </>
          )}
        </em>
      </p>

      {showApplyCreditForm && (
        <div className="mtl" style={{ maxWidth: 300 }}>
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Input
              required
              name="code"
              inputRef={register}
              label="Redeem Gift Card Or Enter Promo Code"
              placeholder="Enter Code"
              addonAfter={
                <LinkButton
                  className="input-group-addon--submit man pan"
                  type="submit"
                >
                  {submitting ? <Spinner size="small" /> : 'Apply'}
                </LinkButton>
              }
            />
          </Form>
        </div>
      )}

      {!showApplyCreditForm && (
        <LinkButton
          className="pan text-bold text-brand-info mbl"
          onClick={() => setShowApplyCreditForm(!showApplyCreditForm)}
        >
          Redeem a Babylist Shop Gift Card
        </LinkButton>
      )}

      <section className="mtxl">
        <Tabs
          id="Tabs with Credit History / Group Gift Funds"
          animation={false}
          defaultActiveKey={1}
        >
          <Tab eventKey={1} title="Credits History">
            <CreditHistoryTabContent
              activeCredits={activeCredits}
              lineItems={lineItems}
            />
          </Tab>

          <Tab eventKey={2} title="Group Gift Funds">
            <GroupGiftFundsTabContent
              groupGiftTotalFundedAmount={groupGiftTotalFundedAmount}
              groupGifts={groupGifts}
              creditBalance={balance}
              setUserCredit={setUserCredit}
              isOwner={isOwner}
            />
          </Tab>
        </Tabs>
      </section>
    </div>
  )
}

GiftCardsAndPromotions.propTypes = {
  balance: PropTypes.number.isRequired,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      amount: PropTypes.number.isRequired,
      createdAt: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
      detailsUrl: PropTypes.string,
      displayType: PropTypes.string,
    })
  ),
  activeCredits: PropTypes.arrayOf(
    PropTypes.shape({
      balance: PropTypes.number.isRequired,
      campaignName: PropTypes.string,
      expiresAt: PropTypes.string,
      giftCardCode: PropTypes.string,
      isPromotional: PropTypes.bool.isRequired,
      isReturn: PropTypes.bool.isRequired,
    })
  ),
  groupGiftTotalFundedAmount: PropTypes.number.isRequired,
  groupGifts: PropTypes.arrayOf(
    PropTypes.shape({
      regItemId: PropTypes.number.isRequired,
      fundsRedeemedAmount: PropTypes.number.isRequired,
      fundedAmount: PropTypes.number.isRequired,
      isActive: PropTypes.bool.isRequired,
      isRegItemActive: PropTypes.bool.isRequired,
      title: PropTypes.string.isRequired,
      userCreditBalance: PropTypes.number.isRequired,
      unredeemedFunds: PropTypes.array.isRequired,
    })
  ).isRequired,
  isOwner: PropTypes.bool.isRequired,
  setUserCredit: PropTypes.func.isRequired,
}

GiftCardsAndPromotions.defaultProps = {
  activeCredits: [],
  lineItems: [],
}

const mapStateToProps = (state) => {
  return {
    ...state.userCredit,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    setUserCredit: ({ userCredit }) => {
      dispatch({
        type: SET_USER_CREDIT,
        userCredit,
      })
    },
  }
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(GiftCardsAndPromotions)
